import React, { useCallback, useEffect, useState } from 'react';
import StarIcon from '@mui/icons-material/Star';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { MasterCropData } from '../../types';
import {
  clearImageFailure,
  ensureImageCached,
  getCachedBlobUrl,
  getCachedImageStatus,
  getCachedImageUrl,
  markImageFailed,
  markImageLoaded,
  retryImageFetch,
  subscribeToImageCache,
  ZoomMeta,
} from '../../hooks/useZoomPreview';

export interface CropThumbnailProps {
  crop: MasterCropData;
  sectionType: 'front' | 'non-front' | 'carton';
  size?: number;
  isSelected?: boolean;
  isRepresentative?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  isPending?: boolean;
  isLinkHighlighted?: boolean;
  highlightColor?: string;
  highlightGlow?: string;
  showTriangle?: boolean;
  isRecentlyMoved?: boolean;
  reloadVersion?: number;
  topLeftBadgeMode?: 'number' | 'warning' | null;
  topLeftBadgeLabel?: string;
  topLeftBadgeBackground?: string;
  topLeftBadgeBorder?: string;
  topLeftBadgeColor?: string;
  sourceBadgeLabel?: 'S' | 'F' | 'C' | 'O';
  typeBadgeLabel?: 'P' | 'C';
  onClick?: (e: React.MouseEvent) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}

export const CropThumbnail: React.FC<CropThumbnailProps> = ({
  crop,
  sectionType,
  size = 46,
  isSelected = false,
  isRepresentative = false,
  isFocused = false,
  isDisabled = false,
  isPending = false,
  isLinkHighlighted = false,
  highlightColor = 'var(--purple)',
  highlightGlow = '0 0 12px rgba(188,140,255,0.6), 0 0 24px rgba(188,140,255,0.25)',
  showTriangle = false,
  isRecentlyMoved = false,
  reloadVersion = 0,
  topLeftBadgeMode = null,
  topLeftBadgeLabel,
  topLeftBadgeBackground,
  topLeftBadgeBorder,
  topLeftBadgeColor,
  sourceBadgeLabel,
  typeBadgeLabel,
  onClick,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const isInactive = crop.isInactive;
  const [imgError, setImgError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => getCachedImageUrl(crop.imageUrl));

  const meta: ZoomMeta = {
    id: crop.id,
    source: crop.source,
    barcode: crop.barcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage: topLeftBadgeMode === 'warning' ? 'Barcode does not match known barcodes' : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  };

  const handleImgError = useCallback(() => {
    if (getCachedImageStatus(crop.imageUrl) === 'pending') {
      setRetrying(true);
      return;
    }
    markImageFailed(crop.imageUrl);
    setImgError(true);
    setRetrying(false);
  }, [crop.imageUrl]);

  const handleImgLoad = useCallback(() => {
    markImageLoaded(crop.imageUrl);
    setImgError(false);
    setRetrying(false);
  }, [crop.imageUrl]);

  const handleRetry = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setRetrying(true);
    clearImageFailure(crop.imageUrl);
    setImgError(false);
    setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
    retryImageFetch(crop.imageUrl, true);
  }, [crop.imageUrl]);

  useEffect(() => {
    setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
    setImgError(false);
    setRetrying(false);
    ensureImageCached(crop.imageUrl);
    setRetrying(getCachedImageStatus(crop.imageUrl) === 'pending');

    const unsubscribe = subscribeToImageCache(crop.imageUrl, () => {
      const blobUrl = getCachedBlobUrl(crop.imageUrl);
      if (blobUrl) {
        setImageSrc(blobUrl);
        setImgError(false);
        setRetrying(false);
        return;
      }

      if (getCachedImageStatus(crop.imageUrl) === 'pending') {
        setRetrying(true);
        return;
      }

      if (getCachedImageStatus(crop.imageUrl) === 'failed') {
        setRetrying(false);
        setImgError(true);
      }
    });

    return unsubscribe;
  }, [crop.imageUrl]);

  useEffect(() => {
    if (reloadVersion === 0) return;
    clearImageFailure(crop.imageUrl);
    retryImageFetch(crop.imageUrl, true);
    setImgError(false);
    setRetrying(true);
    setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
  }, [reloadVersion, crop.imageUrl]);

  const triangleColor = crop.isBadImage
    ? 'var(--danger)'
    : sectionType === 'non-front' || sectionType === 'carton'
      ? 'var(--warning)'
      : crop.isMismatch
        ? 'var(--warning)'
        : 'var(--positive)';

  const triSize = size > 72 ? 16 : 14;
  const iconSize = Math.max(14, Math.round(size * 0.35));
  const isImageLoading = retrying && !imgError;
  const isFiveMp = crop.imageResolution?.toLowerCase() === '5mp';
  const showFocusStyle = isLinkHighlighted || isFocused;
  const focusBorder = isLinkHighlighted ? `3px solid ${highlightColor}` : '3px solid var(--purple)';
  const focusShadow = isLinkHighlighted ? highlightGlow : '0 0 12px rgba(188,140,255,0.6), 0 0 24px rgba(188,140,255,0.25)';
  const hoverTitle = `classification_type: ${crop.classificationType ?? 'unknown'}`;
  const fiveMpTopOffset = isImageLoading ? 20 : isRepresentative ? 16 : 3;
  const resolvedSourceBadgeLabel =
    sourceBadgeLabel
      || (crop.source === 'scan'
        ? 'S'
        : crop.source === 'forge'
          ? 'F'
          : crop.source === 'control'
            ? 'C'
            : 'O');
  const resolvedTypeBadgeLabel =
    typeBadgeLabel || (sectionType === 'carton' || crop.classificationType === 'carton' ? 'C' : 'P');

  const sourceBadgeStyles =
    resolvedSourceBadgeLabel === 'S' || resolvedSourceBadgeLabel === 'F'
      ? {
          background: 'rgba(63, 185, 80, 0.92)',
          border: '1px solid rgba(164, 239, 176, 0.55)',
          color: '#f3fff5',
        }
      : {
          background: 'rgba(107, 114, 128, 0.92)',
          border: '1px solid rgba(209, 213, 219, 0.45)',
          color: '#f9fafb',
        };

  const typeBadgeStyles =
    resolvedTypeBadgeLabel === 'P'
      ? {
          background: 'rgba(63, 185, 80, 0.92)',
          border: '1px solid rgba(164, 239, 176, 0.55)',
          color: '#f3fff5',
        }
      : {
          background: 'rgba(107, 114, 128, 0.92)',
          border: '1px solid rgba(209, 213, 219, 0.45)',
          color: '#f9fafb',
        };

  return (
    <div
      data-crop-id={crop.id}
      title={hoverTitle}
      onClick={onClick}
      onMouseEnter={onImageHover ? (e) => onImageHover(e, crop.imageUrl, meta) : undefined}
      onMouseMove={onImageMove ? (e) => onImageMove(e, crop.imageUrl, meta) : undefined}
      onMouseLeave={onImageLeave}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        cursor: isDisabled ? 'default' : 'pointer',
        border: isSelected
          ? 'var(--crop-selected-border-width) solid var(--crop-selected-border)'
          : showFocusStyle
            ? focusBorder
            : isInactive
              ? '1px dashed var(--text-tertiary)'
              : isPending
                ? '1px dashed var(--text-tertiary)'
                : '2px solid var(--border)',
        boxShadow: isSelected
          ? 'var(--crop-selected-shadow)'
          : showFocusStyle
            ? focusShadow
            : 'none',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'all 120ms ease',
        flexShrink: 0,
        animation: isRecentlyMoved
          ? 'cropLanded 1.8s ease-out forwards'
          : showFocusStyle ? 'pulse-glow 1.5s ease-in-out infinite' : undefined,
        transform: showFocusStyle ? 'scale(1.08)' : undefined,
        zIndex: showFocusStyle ? 2 : undefined,
      }}
    >
      {imgError ? (
        <div
          onClick={handleRetry}
          style={{
            width: '100%', height: '100%', background: '#1a1a1a',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, cursor: 'pointer',
          }}
          title="Click to retry"
        >
          <BrokenImageOutlinedIcon style={{
            fontSize: iconSize,
            color: retrying ? 'var(--accent)' : 'var(--text-tertiary)',
            opacity: retrying ? 1 : 0.5,
            animation: retrying ? 'pulse-glow 1s ease-in-out infinite' : undefined,
          }} />
        </div>
      ) : (
        <img
          src={imageSrc}
          alt={crop.id}
          style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block' }}
          draggable={false}
          onLoad={handleImgLoad}
          onError={handleImgError}
        />
      )}
      {isImageLoading && (
        <div
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
            width: Math.max(14, Math.round(size * 0.28)),
            height: Math.max(14, Math.round(size * 0.28)),
            borderRadius: 999,
            background: 'rgba(15, 15, 15, 0.78)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
          title="Image loading"
        >
          <AutorenewOutlinedIcon
            style={{
              fontSize: Math.max(10, Math.round(size * 0.18)),
              color: 'var(--accent)',
              animation: 'forge-spin 0.9s linear infinite',
            }}
          />
        </div>
      )}
      {isFiveMp && (
        <div
          style={{
            position: 'absolute',
            top: fiveMpTopOffset,
            right: 3,
            minWidth: Math.max(15, Math.round(size * 0.36)),
            height: Math.max(9, Math.round(size * 0.17)),
            padding: '0 3px',
            borderRadius: 999,
            background: 'rgba(201, 45, 66, 0.96)',
            border: '1px solid rgba(255, 162, 172, 0.58)',
            color: '#fff5f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(5, Math.round(size * 0.1)),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.03em',
          }}
          title="5MP crop"
        >
          5MP
        </div>
      )}
      {topLeftBadgeMode && (
        <div style={{
          position: 'absolute',
          left: 3,
          top: 3,
          pointerEvents: 'none',
        }}>
          <div
            style={{
              minWidth: Math.max(10, Math.round(size * 0.19)),
              height: Math.max(10, Math.round(size * 0.19)),
              padding: '0 2px',
              borderRadius: 999,
              background: topLeftBadgeMode === 'number'
                ? (topLeftBadgeBackground || 'rgba(88, 166, 255, 0.92)')
                : 'rgba(210, 153, 34, 0.96)',
              border: topLeftBadgeMode === 'number'
                ? (topLeftBadgeBorder || '1px solid rgba(145, 193, 255, 0.55)')
                : '1px solid rgba(255, 220, 120, 0.6)',
              color: topLeftBadgeMode === 'number'
                ? (topLeftBadgeColor || '#f3f9ff')
                : '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: Math.max(7, Math.round(size * 0.11)),
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {topLeftBadgeMode === 'warning' ? (
              <WarningAmberOutlinedIcon style={{ fontSize: Math.max(7, Math.round(size * 0.13)) }} />
            ) : (
              topLeftBadgeLabel || '?'
            )}
          </div>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 3,
          bottom: 3,
          minWidth: Math.max(10, Math.round(size * 0.19)),
          height: Math.max(10, Math.round(size * 0.19)),
          padding: '0 2px',
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          fontFamily: 'var(--font-mono)',
          fontSize: Math.max(7, Math.round(size * 0.11)),
          fontWeight: 700,
          lineHeight: 1,
          ...sourceBadgeStyles,
        }}
      >
        {resolvedSourceBadgeLabel}
      </div>
      <div
        style={{
          position: 'absolute',
          right: 3,
          bottom: 3,
          minWidth: Math.max(10, Math.round(size * 0.19)),
          width: Math.max(10, Math.round(size * 0.19)),
          height: Math.max(10, Math.round(size * 0.19)),
          padding: '0 2px',
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          fontFamily: 'var(--font-mono)',
          fontSize: Math.max(7, Math.round(size * 0.11)),
          fontWeight: 700,
          lineHeight: 1,
          ...typeBadgeStyles,
        }}
      >
        {resolvedTypeBadgeLabel}
      </div>
      {/* Crop type triangle (demo only) */}
      {showTriangle && (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: 0, height: 0,
          borderTop: `${triSize}px solid ${triangleColor}`,
          borderRight: `${triSize}px solid transparent`,
          pointerEvents: 'none',
        }} />
      )}
      {/* Representative star */}
      {isRepresentative && (
        <StarIcon style={{
          position: 'absolute', top: 2, right: 2, fontSize: 12,
          color: '#E8976C', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))',
        }} />
      )}
      {isSelected && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(232,151,108,0.15)', pointerEvents: 'none' }} />
      )}
    </div>
  );
};
